import { matchPath } from 'react-router-dom';

// The route list is read from the router's own source at build time instead of being copied here.
// Home declares its routes as inline <Route> elements (react-router v5, no route array), and a hand copy
// would drift the first time someone adds a page; a page missing from a copy would be reported
// under its raw path, uuid and all. Cost: the raw text of both files (~50 KB, unminified build)
// ships in the bundle, and only string-literal `path="..."` attributes written as
// `<Route [exact] path=...` are seen. route_patterns.test.js fails if any <Route> stops matching.
// App first, then Home: the same order the two <Switch>es are walked in.
export const ROUTER_SOURCES = import.meta.glob(['../../App.jsx', '../home/index.jsx'], {
  query: '?raw',
  import: 'default',
  eager: true
});

const ROUTE_RE = /<Route\s+(exact\s+)?path="([^"]+)"/g;

export const extractRoutes = (source) =>
  [...source.matchAll(ROUTE_RE)].map((m) => ({ path: m[2], exact: Boolean(m[1]) }));

// App's `path="*"` hands everything else to Home, so it is never the answer for a page.
export const ROUTES = [
  ROUTER_SOURCES['../../App.jsx'],
  ROUTER_SOURCES['../home/index.jsx']
].flatMap((source) => extractRoutes(source).filter((r) => r.path !== '*'));

const UUID_SHAPE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const UUID_RE = new RegExp(`^${UUID_SHAPE}$`, 'i');
export const isUuid = (value) => typeof value === 'string' && UUID_RE.test(value);

// A path segment or control name is replaced when it can identify a person or a row: a uuid, a
// run of 3+ digits (row ids, and phone fragments from the Twilio dialer), or anything email-like
// (`@` or its encoding %40). Everything else in a path is a literal the code wrote.
const IDENTIFYING_RUN = new RegExp(`${UUID_SHAPE}|\\S*(@|%40)\\S*|\\d{3,}`, 'gi');
export const scrub = (text) => text.replace(IDENTIFYING_RUN, ':id');

// goserver's ingest drops an event whose route is over 256 chars (counted as dropped, the rest of
// the batch is kept), so that view would be lost; an undeclared deep path is cut rather than sent.
const MAX_PATH = 256;
export const normalizePath = (pathname) =>
  pathname
    .split('/')
    .map((segment) => (segment ? scrub(segment) : segment))
    .join('/')
    .slice(0, MAX_PATH);

// goserver keeps module, control, control_kind and entity_type only when they match
// ^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$ and silently drops them otherwise. An id like `save notes` or
// a scrubbed `:id` would arrive as a nameless control, so it is fitted to the pattern here.
export const asLabel = (text) =>
  text
    .replace(/[^A-Za-z0-9_.:-]+/g, '_')
    .replace(/^[^A-Za-z0-9]+/, '')
    .slice(0, 64);

// Pages whose declared route has no uuid param and that take their entity from the query string
// instead. This is the only place the query is read: just the named param, kept only when
// uuid-shaped, and the query string itself is never recorded. A page added here must be a declared
// route's `path=` exactly as Home writes it (the key is the matched pattern, not the URL), or it
// never matches. Counts are links built that way in src/, tests excluded, on 2026-10-26.
const QUERY_ENTITIES = {
  // `<Route exact path="/studentprofile">`, 90 links build `/studentprofile?id=<student uuid>`:
  // who viewed a student is the main question this feature answers.
  '/studentprofile': ['id', 'student'],
  // The student profile's own buttons open these with `?id=<student uuid>&name=...`
  // (student_profile/index.jsx open_transactions, open_credit_history,
  // openMembershipCreditHistory, go_to_roadmap, open_email_logs; ImmediateDowngrade.tsx). Without
  // them a Care user's look at a student's money or emails would not reach the CMS Views tab.
  '/studenttransactions': ['id', 'student'],
  '/credithistory': ['id', 'student'],
  '/membership_credits_history': ['id', 'student'],
  '/addroadmap': ['id', 'student'],
  '/studentemaillogs': ['id', 'student'],
  // 10 links build `/personalised-plan?student_uuid=<student uuid>`.
  '/personalised-plan': ['student_uuid', 'student'],
  // 21 links build `/usersessions/<past|future>?id=<uuid>&user_type=STUDENT|TEACHER`: the same page
  // serves both, so the third element names the param that says which; any other value records
  // no entity rather than a guessed one.
  '/usersessions/past': ['id', null, 'user_type'],
  '/usersessions/future': ['id', null, 'user_type'],
  // 99 links build `/teacherprofile?id=<teacher uuid>`.
  '/teacherprofile': ['id', 'teacher'],
  // 1 link builds `/teacherprofile/payment-details?id=<teacher uuid>`: a teacher's payout data.
  '/teacherprofile/payment-details': ['id', 'teacher'],
  // 22 links build `/sessiondetails?id=<session uuid>`.
  '/sessiondetails': ['id', 'session'],
  // 12 links build `/groupsessiondetails?session_uuid=<session uuid>`, the one page whose param
  // is not `id`.
  '/groupsessiondetails': ['session_uuid', 'session']
};

const USER_TYPES = { student: 'student', teacher: 'teacher' };
const userType = (value) => USER_TYPES[String(value || '').toLowerCase()];

// `/trial_points/:teacher_uuid` names its entity; `/user_timeline/:user_type/:uuid` names it in a
// sibling param; any other bare `:uuid` (`/workshop/:uuid`) takes the module.
const entityFromParams = (params, module) => {
  const key = Object.keys(params).find((k) => /uuid$/i.test(k) && isUuid(params[k]));
  if (!key) return {};
  const type = key.replace(/_?uuid$/i, '') || userType(params.user_type) || module;
  return { entity_type: type, entity_uuid: params[key] };
};

const entityFromQuery = (route, search) => {
  const spec = QUERY_ENTITIES[route];
  if (!spec) return {};
  const query = new URLSearchParams(search);
  const value = query.get(spec[0]);
  const type = spec[2] ? userType(query.get(spec[2])) : spec[1];
  return isUuid(value) && type ? { entity_type: type, entity_uuid: value } : {};
};

// First declared route that matches, honouring `exact`, which is how <Switch> picks the page.
// An undeclared path falls back to itself with identifying segments replaced, never the raw path.
export const resolveRoute = (pathname, search = '') => {
  let match = null;
  ROUTES.some((r) => {
    match = matchPath(pathname, r);
    return match;
  });
  // react-router's param modifiers (`:tab?`, `:rest*`, `:rest+`) are syntax, not part of the page's
  // name, and goserver's ingest drops any event whose route contains `?`, so every event on
  // `/teacher-payments/:tab?` (Home) would be lost. Stripped here, the one place every route is
  // named, so no matched pattern reaches the wire with one.
  const route = match ? match.path.replace(/(:[A-Za-z_]+)[?*+]/g, '$1') : normalizePath(pathname);
  const module = asLabel(route.split('/')[1] || '') || 'root';
  const entity = match ? entityFromParams(match.params, module) : {};
  return { route, module, ...(entity.entity_uuid ? entity : entityFromQuery(route, search)) };
};
